import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Form, Button, Alert, Spinner } from 'react-bootstrap'
import { useAuthStore } from '../store/authStore'

export default function Login() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [tempToken, setTempToken] = useState<string | null>(null)
  const [requires2fa, setRequires2fa] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const login = useAuthStore(state => state.login)
  const login2fa = useAuthStore(state => state.login2fa)
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const result = await login(username, password)

      if (result.requires_2fa && result.temp_token) {
        setRequires2fa(true)
        setTempToken(result.temp_token)
        setLoading(false)
        return
      }

      navigate('/')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Anmeldung fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  const handle2faSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (!tempToken) {
        setError('Sitzung abgelaufen. Bitte erneut anmelden.')
        setRequires2fa(false)
        setTempToken(null)
        return
      }

      await login2fa(tempToken, totpCode)
      navigate('/')
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } } }
      setError(error.response?.data?.detail || 'Verifizierung fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    setRequires2fa(false)
    setTempToken(null)
    setTotpCode('')
    setError('')
  }

  // 2FA verification screen
  if (requires2fa) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-logo">
            <i className="bi bi-shield-lock"></i>
            <h2 className="mt-3">2-Faktor-Authentifizierung</h2>
            <p className="text-muted">Geben Sie den Code aus Ihrer Authenticator-App ein</p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handle2faSubmit}>
            <Form.Group className="mb-4">
              <Form.Label>Verifizierungscode</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                required
                autoFocus
                className="text-center fs-4 letter-spacing-2"
                style={{ letterSpacing: '0.5em' }}
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-3"
              disabled={loading || totpCode.length !== 6}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Verifizieren...
                </>
              ) : (
                'Verifizieren'
              )}
            </Button>

            <Button
              variant="link"
              type="button"
              className="w-100"
              onClick={handleBack}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Zurück zur Anmeldung
            </Button>
          </Form>
        </div>
      </div>
    )
  }

  // Normal login screen
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <i className="bi bi-headset"></i>
          <h2 className="mt-3">it64Pbx</h2>
          <p className="text-muted">Asterisk Web Interface</p>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Benutzername</Form.Label>
            <Form.Control
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label>Passwort</Form.Label>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Anmelden...
              </>
            ) : (
              'Anmelden'
            )}
          </Button>

          <div className="text-center mt-3">
            <Link to="/forgot-password" className="text-muted">
              Passwort vergessen?
            </Link>
          </div>
        </Form>
      </div>
    </div>
  )
}
